import { useLazyQuery } from '@apollo/client';
import { GET_CERTIFICATE_DETAILS } from '../../../graph-queries/tar/certificate';
import * as ExcelJS from 'exceljs';
import bpImg from '../../../lib/images/bp.png';
import { TCertificate } from '../../../lib/types';
import { TJoint } from '../../../lib/types';
import { extractAndFormatName } from '../../organisms/AspectsTable/util';

const MINIMUM_JOINTS_COUNT = 12;
const TOTAL_COLUMN_COUNT_FOR_JI1 = 28;
const TOTAL_COLUMN_COUNT_FOR_SBT = 22;

const SIZE_SMALL = 8;
const SIZE_MEDIUM = 16;
const SIZE_BIG = 32;
const SIZE_LARGE = 48;
const SIZE_HUGE = 64;

const HEADER_CELL_FONT = { size: 6, family: 2, name: 'Calibri' };

const EMPTY_JOINT: TJoint = {
  jointId: -1,
  certificateId: -1,
  jointNumber: '',
  additionalDetails: '',
  tagId: '',
  pIdNo: '',
  flangeSize: '',
  rating: '',
  boltDiameter: '',
  boltQuantity: '',
  boltLength: '',
  boltMaterial: '',
  gasketType: '',
  torqueValue: '',
  hydraulicToolRef: '',
  hydraulicToolPass: '',
  hydraulicToolPumpPressure: '',
  updatedAt: -1,
  updatedBy: '',
  hasFlangeDefect: false,
  actualTorque: '',
  supervisedAssembly: false,
};

export const useCertificateForExport = () => {
  const [fetchCertificate] = useLazyQuery(GET_CERTIFICATE_DETAILS, {
    fetchPolicy: 'network-only', // ensure API is called every time button is clicked
  });

  const fetchDetails = async (certificateId: number) => {
    const { data: freshData } = await fetchCertificate({ variables: { certificateId } });

    if (freshData?.certificateDetails) {
      await createWorkbookFromData(freshData?.certificateDetails as TCertificate);
    }
  };

  return fetchDetails;
};

const createWorkbookFromData = async (certificate: TCertificate) => {
  const workbook = new ExcelJS.Workbook();
  const fileName = certificate?.title || 'GOC';
  const tabName = certificate.type === 'JI1' ? 'JIM Certificate' : 'JIM-SBT Certificate';
  const sheet = workbook.addWorksheet(fileName, {
    pageSetup: {
      paperSize: 8 as ExcelJS.PaperSize,
      orientation: 'landscape',
      margins: {
        left: 0.7,
        right: 0.7,
        top: 0.7,
        bottom: 0.7,
        header: 0,
        footer: 0,
      },
      fitToPage: true,
    },
  });

  setupColumnSizes(sheet, certificate.type);

  let currentRow = 0;
  currentRow = await setupMetadata(sheet, certificate);
  currentRow = await setupColumns(sheet, currentRow, certificate.type);

  for (const joint of certificate.tarJoints || []) {
    currentRow = await addJoint(sheet, joint, currentRow, certificate.type);
  }

  // Add empty rows to fill the sheet
  for (let i = certificate?.tarJoints?.length || 0; i < MINIMUM_JOINTS_COUNT; i++) {
    currentRow = await addJoint(sheet, EMPTY_JOINT, currentRow);
  }

  await addFooter(sheet, currentRow, certificate.type);

  sheet.eachRow((row) =>
    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    })
  );

  await downloadWorkbook(workbook, fileName);
};

const setupColumnSizes = (sheet: ExcelJS.Worksheet, type: string) => {
  const TOTAL_COLUMN_COUNT =
    type === 'SBT' ? TOTAL_COLUMN_COUNT_FOR_SBT : TOTAL_COLUMN_COUNT_FOR_JI1;
  for (let i = 1; i <= TOTAL_COLUMN_COUNT; i++) {
    sheet.getColumn(i).width = SIZE_SMALL;
  }

  if (type === 'JI1') {
    ['A', 'B', 'M', 'P', 'S', 'V', 'W', 'X', 'Y', 'Z', 'AA', 'AB'].forEach((c) => {
      sheet.getColumn(c).width = SIZE_MEDIUM;
    });
  } else {
    ['A', 'B', 'E', 'H', 'L', 'O', 'R', 'S', 'T', 'U', 'V'].forEach((c) => {
      sheet.getColumn(c).width = SIZE_MEDIUM;
    });
  }

  ['C'].forEach((c) => {
    sheet.getColumn(c).width = SIZE_LARGE;
  });
};

const setupMetadata = async (sheet: ExcelJS.Worksheet, certificate: TCertificate) => {
  const img1 = sheet.workbook.addImage({
    buffer: await fetch(bpImg).then((res) => res.arrayBuffer()),
    extension: 'png',
  });

  const typeIsJI1 = certificate.type === 'JI1' ? true : false;

  // Logo
  sheet.mergeCells('A2:B3');
  sheet.addImage(img1, {
    tl: { col: 0.9999, row: 1.2 },
    ext: { width: 72, height: 72 },
  });
  sheet.getColumn('A').width = 10;
  sheet.getColumn('B').width = 10;
  sheet.getRows(2, 2)?.forEach((r) => (r.height = 20));

  // Metadata
  if (typeIsJI1) {
    sheet.mergeCells('A4:B5');
    sheet.getCell('A4').value = `Cert #${certificate.certificateNumber ?? certificate.certificateId}`;

    sheet.mergeCells('C2:N2');
    sheet.getCell('C2').value = `Certificate Title: ${certificate.title}`;
    sheet.mergeCells('C3:N3');
    sheet.getCell('C3').value = `Maintenance Order: ${certificate.maintenanceOrder}`;
    sheet.mergeCells('C4:N4');
    sheet.getCell('C4').value = `Created By: ${extractAndFormatName(certificate.createdBy)}`;
    sheet.mergeCells('C5:L5');
    sheet.getCell('C5').value = typeIsJI1 ? `Lubricant Used: ${certificate.lubricantUsed}` : '';

    sheet.mergeCells('O2:R2');
    sheet.getCell('O2').value = `System: ${certificate.system}`;
    sheet.mergeCells('O3:R3');
    sheet.getCell('O3').value = `Location: ${certificate.location}`;
    sheet.mergeCells('O4:V4');
    sheet.getCell('O4').value = `Checked By: ${certificate.checkedBy}`;
    sheet.mergeCells('S2:V2');
    sheet.getCell('S2').value = `Permit Number: ${certificate.permitNumber}`;
    sheet.mergeCells('S3:V3');
    sheet.getCell('S3').value = `Isolation Number: ${certificate.isolationNumber}`;

    ['A4', 'C2', 'C3', 'C4', 'C5', 'O2', 'O3', 'O4', 'S2', 'S3'].forEach(
      (c) =>
        (sheet.getCell(c).alignment = {
          vertical: 'middle',
          horizontal: 'left',
        })
    );
    sheet.mergeCells('W2:AB4');
    sheet.getCell('W2').value = 'North Sea Joint Integrity Management -JIM';

    // Stages
    sheet.mergeCells('M5:V5');
    sheet.getCell('M5').value = 'PART A - Installation';
    sheet.getCell('W5').value = 'PART B - Leak/Service Test';
    sheet.mergeCells('X5:AB5');
    sheet.getCell('X5').value = 'PART C - Operational Phase';
    ['M5', 'W5', 'X5'].forEach(
      (c) =>
        (sheet.getCell(c).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFBFBFBF' },
        })
    );

    ['M5', 'W5', 'X5', 'W2'].forEach(
      (c) =>
        (sheet.getCell(c).alignment = {
          vertical: 'middle',
          horizontal: 'center',
        })
    );
  } else {
    sheet.mergeCells('A4:B5');
    sheet.getCell('A4').value = `Cert #${certificate.certificateNumber ?? certificate.certificateId}`;

    sheet.mergeCells('C2:F2');
    sheet.getCell('C2').value = `Certificate Title: ${certificate.title}`;
    sheet.mergeCells('C3:F3');
    sheet.getCell('C3').value = `Maintenance Order: ${certificate.maintenanceOrder}`;
    sheet.mergeCells('C4:F4');
    sheet.getCell('C4').value = `Created By: ${extractAndFormatName(certificate.createdBy)}`;
    sheet.mergeCells('C5:D5');
    sheet.getCell('C5').value = '';

    sheet.mergeCells('G2:K2');
    sheet.getCell('G2').value = `System: ${certificate.system}`;
    sheet.mergeCells('G3:K3');
    sheet.getCell('G3').value = `Location: ${certificate.location}`;
    sheet.mergeCells('G4:N4');
    sheet.getCell('G4').value = `Checked By: ${certificate.checkedBy}`;
    sheet.mergeCells('L2:N2');
    sheet.getCell('L2').value = `Permit Number: ${certificate.permitNumber}`;
    sheet.mergeCells('L3:N3');
    sheet.getCell('L3').value = `Isolation Number: ${certificate.isolationNumber}`;

    ['A4', 'C2', 'C3', 'C4', 'C5', 'G2', 'G3', 'G4', 'L2', 'L3'].forEach(
      (c) =>
        (sheet.getCell(c).alignment = {
          vertical: 'middle',
          horizontal: 'left',
        })
    );
    sheet.mergeCells('O2:V4');
    sheet.getCell('O2').value = 'North Sea Joint Integrity Management -SBT';

    // Stages
    sheet.mergeCells('E5:N5');
    sheet.getCell('E5').value = 'PART A - Installation';
    sheet.mergeCells('O5:Q5');
    sheet.getCell('O5').value = 'PART B - Leak/Service Test';
    sheet.mergeCells('R5:V5');
    sheet.getCell('R5').value = 'PART C - Operational Phase';
    ['E5', 'O5', 'R5'].forEach(
      (c) =>
        (sheet.getCell(c).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFBFBFBF' },
        })
    );

    ['E5', 'O5', 'R5', 'O2'].forEach(
      (c) =>
        (sheet.getCell(c).alignment = {
          vertical: 'middle',
          horizontal: 'center',
        })
    );
  }

  sheet.getRows(2, 4)?.forEach((row) => (row.height = SIZE_BIG));

  return 6;
};

const setupColumns = async (sheet: ExcelJS.Worksheet, rowNumber: number, type: string) => {
  const row = sheet.getRow(rowNumber);
  const nextRow = sheet.getRow(rowNumber + 1);
  const extraRow = sheet.getRow(rowNumber + 2);
  const typeIsJI1 = type === 'JI1' ? true : false;

  if (typeIsJI1) {
    row.getCell('A').value = 'Joint number';
    nextRow.getCell('A').merge(row.getCell('A'));
    extraRow.getCell('A').merge(row.getCell('A'));

    row.getCell('B').value = 'TAG ID';
    nextRow.getCell('B').merge(row.getCell('B'));
    extraRow.getCell('B').merge(row.getCell('B'));

    row.getCell('C').value = 'P&ID No';
    nextRow.getCell('C').value = 'Additional details (Iso No / Joint Ref)';
    extraRow.getCell('C').merge(nextRow.getCell('C'));

    row.getCell('D').value = 'Flange Size (NB)';
    nextRow.getCell('D').value = 'Rating';
    extraRow.getCell('D').merge(nextRow.getCell('D'));

    row.getCell('E').value = 'Bolt Ø (inches)';
    nextRow.getCell('E').value = 'Bolt Qty';
    extraRow.getCell('E').merge(nextRow.getCell('E'));

    row.getCell('F').value = 'Bolt Length (mm)';
    nextRow.getCell('F').merge(row.getCell('F'));
    extraRow.getCell('F').merge(row.getCell('F'));

    row.getCell('G').value = 'Bolt/Nut Material';
    nextRow.getCell('G').merge(row.getCell('G'));
    extraRow.getCell('G').merge(row.getCell('G'));

    row.getCell('H').value = 'Gasket Type';
    nextRow.getCell('H').merge(row.getCell('H'));
    extraRow.getCell('H').merge(row.getCell('H'));

    row.getCell('I').value = 'Torque Value';
    nextRow.getCell('I').merge(row.getCell('I'));
    extraRow.getCell('I').merge(row.getCell('I'));

    row.getCell('K').merge(row.getCell('J'));
    row.getCell('L').merge(row.getCell('J'));
    row.getCell('J').value = 'Hydraulic tool details';

    nextRow.getCell('J').value = 'Tool ref';
    extraRow.getCell('J').merge(nextRow.getCell('J'));

    nextRow.getCell('K').value = 'Pass';
    extraRow.getCell('K').merge(nextRow.getCell('K'));

    nextRow.getCell('L').value = 'Pump Pressure (PSI)';
    extraRow.getCell('L').merge(nextRow.getCell('L'));

    // Blue columns
    row.getCell('M').value = 'Joint Broken and Inspected';
    row.getCell('N').merge(row.getCell('M'));
    row.getCell('O').value = 'Defect Identified (Y/N)';
    nextRow.getCell('O').merge(row.getCell('O'));
    extraRow.getCell('O').merge(row.getCell('O'));
    nextRow.getCell('M').value = 'Name';
    extraRow.getCell('M').value = 'Signature';
    nextRow.getCell('N').value = 'Date';
    extraRow.getCell('N').merge(nextRow.getCell('N'));
    [
      row.getCell('M'),
      row.getCell('O'),
      nextRow.getCell('M'),
      nextRow.getCell('N'),
      extraRow.getCell('M'),
    ].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0000FF' },
        })
    );

    // Red columns
    row.getCell('P').value = 'Joint Assembled and inspected';
    row.getCell('Q').merge(row.getCell('P'));
    nextRow.getCell('P').value = 'Name';
    extraRow.getCell('P').value = 'Signature';
    nextRow.getCell('Q').value = 'Date';
    extraRow.getCell('Q').merge(nextRow.getCell('Q'));
    [row.getCell('P'), nextRow.getCell('P'), nextRow.getCell('Q'), extraRow.getCell('P')].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF0000' },
        })
    );

    // Yellow columns
    row.getCell('R').value = 'Joint Tightened';
    row.getCell('S').merge(row.getCell('R'));
    row.getCell('T').merge(row.getCell('R'));
    nextRow.getCell('R').value = 'Actual Torque  Value + Units';
    extraRow.getCell('R').merge(nextRow.getCell('R'));
    nextRow.getCell('S').value = 'Completion Name';
    extraRow.getCell('S').value = 'Completion Signature';
    nextRow.getCell('T').value = 'Date';
    extraRow.getCell('T').merge(nextRow.getCell('T'));
    [
      row.getCell('R'),
      nextRow.getCell('R'),
      nextRow.getCell('S'),
      nextRow.getCell('T'),
      extraRow.getCell('S'),
    ].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF54' },
        })
    );

    // Brown columns
    row.getCell('U').value = 'Witness Name\nIf required ';
    row.getCell('V').merge(row.getCell('U'));
    nextRow.getCell('U').value = 'S A \nY/N';
    extraRow.getCell('U').merge(nextRow.getCell('U'));
    nextRow.getCell('V').value = 'Witness Signature';
    extraRow.getCell('V').merge(nextRow.getCell('V'));
    [row.getCell('U'), nextRow.getCell('U'), nextRow.getCell('V')].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFAF7441' },
        })
    );

    // Green columns
    row.getCell('W').value = 'Joint Checked By (Name)';
    nextRow.getCell('W').value = 'Witness Signature';
    extraRow.getCell('W').merge(nextRow.getCell('W'));
    [row.getCell('W'), nextRow.getCell('W')].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF377F22' },
        })
    );

    // Empty columns
    row.getCell('X').value = 'Service Test Start-Up Date';
    nextRow.getCell('X').merge(row.getCell('X'));
    extraRow.getCell('X').merge(row.getCell('X'));
    row.getCell('Y').value = '12 Hour Check';
    nextRow.getCell('Y').merge(row.getCell('Y'));
    extraRow.getCell('Y').merge(row.getCell('Y'));
    row.getCell('Z').value = '24 Hour Check';
    nextRow.getCell('Z').merge(row.getCell('Z'));
    extraRow.getCell('Z').merge(row.getCell('Z'));
    row.getCell('AA').value = '36 Hour Check';
    nextRow.getCell('AA').merge(row.getCell('AA'));
    extraRow.getCell('AA').merge(row.getCell('AA'));
    row.getCell('AB').value = '48 Hour Check and Tag Removed';
    nextRow.getCell('AB').merge(row.getCell('AB'));
    extraRow.getCell('AB').merge(row.getCell('AB'));

    [row, nextRow, extraRow].forEach((row) =>
      row.eachCell((cell) => {
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.font = HEADER_CELL_FONT;
      })
    );
    const partsRow = sheet.getRow(rowNumber - 1);
    partsRow.getCell('M').font = HEADER_CELL_FONT;
    partsRow.getCell('W').font = HEADER_CELL_FONT;
    partsRow.getCell('X').font = HEADER_CELL_FONT;
  } else {
    row.getCell('A').value = 'Joint number';
    nextRow.getCell('A').merge(row.getCell('A'));
    extraRow.getCell('A').merge(row.getCell('A'));

    row.getCell('B').value = 'TAG ID';
    nextRow.getCell('B').merge(row.getCell('B'));
    extraRow.getCell('B').merge(row.getCell('B'));

    row.getCell('C').value = 'P&ID No';
    row.getCell('D').merge(row.getCell('C'));
    nextRow.getCell('C').value = 'Joint Description';
    extraRow.getCell('C').merge(nextRow.getCell('C'));
    nextRow.getCell('D').merge(nextRow.getCell('C'));

    // Blue columns
    row.getCell('E').value = 'Joint Broken and Inspected';
    row.getCell('F').merge(row.getCell('E'));
    row.getCell('G').value = 'Defect Identified (Y/N)';
    nextRow.getCell('G').merge(row.getCell('G'));
    extraRow.getCell('G').merge(row.getCell('G'));
    nextRow.getCell('E').value = 'Name';
    extraRow.getCell('E').value = 'Signature';
    nextRow.getCell('F').value = 'Date';
    extraRow.getCell('F').merge(nextRow.getCell('F'));
    [
      row.getCell('E'),
      row.getCell('G'),
      nextRow.getCell('E'),
      nextRow.getCell('F'),
      extraRow.getCell('E'),
    ].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0000FF' },
        })
    );

    // Red columns
    row.getCell('H').value = 'Joint Assembled and inspected';
    row.getCell('I').merge(row.getCell('H'));
    row.getCell('J').merge(row.getCell('H'));
    nextRow.getCell('H').value = 'Name';
    nextRow.getCell('I').merge(nextRow.getCell('H'));
    extraRow.getCell('H').value = 'Signature';
    extraRow.getCell('I').merge(extraRow.getCell('H'));
    nextRow.getCell('J').value = 'Date';
    extraRow.getCell('J').merge(nextRow.getCell('J'));
    [row.getCell('H'), nextRow.getCell('H'), nextRow.getCell('J'), extraRow.getCell('H')].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF0000' },
        })
    );

    // Yellow columns
    row.getCell('K').value = 'Joint Independently Inspected';
    row.getCell('L').merge(row.getCell('K'));
    row.getCell('M').merge(row.getCell('K'));
    row.getCell('N').merge(row.getCell('K'));
    nextRow.getCell('K').value = 'Completion Name';
    nextRow.getCell('L').merge(nextRow.getCell('K'));
    nextRow.getCell('M').merge(nextRow.getCell('K'));
    extraRow.getCell('K').value = 'Completion Signature';
    extraRow.getCell('L').merge(extraRow.getCell('K'));
    extraRow.getCell('M').merge(extraRow.getCell('K'));
    nextRow.getCell('N').value = 'Date';
    extraRow.getCell('N').merge(nextRow.getCell('N'));
    [row.getCell('K'), nextRow.getCell('K'), nextRow.getCell('N'), extraRow.getCell('K')].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF54' },
        })
    );

    // Green columns
    row.getCell('O').value = 'Joint Tested By';
    row.getCell('P').merge(row.getCell('O'));
    nextRow.getCell('O').value = 'Signature';
    extraRow.getCell('O').merge(nextRow.getCell('O'));
    nextRow.getCell('P').merge(nextRow.getCell('O'));
    row.getCell('Q').value = 'Date';
    nextRow.getCell('Q').merge(row.getCell('Q'));
    extraRow.getCell('Q').merge(row.getCell('Q'));
    [row.getCell('O'), nextRow.getCell('O'), row.getCell('Q')].forEach(
      (cell) =>
        (cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF377F22' },
        })
    );

    // Empty columns
    row.getCell('R').value = 'Service Test Start-Up Date';
    nextRow.getCell('R').merge(row.getCell('R'));
    extraRow.getCell('R').merge(row.getCell('R'));
    row.getCell('S').value = '12 Hour Check';
    nextRow.getCell('S').merge(row.getCell('S'));
    extraRow.getCell('S').merge(row.getCell('S'));
    row.getCell('T').value = '24 Hour Check';
    nextRow.getCell('T').merge(row.getCell('T'));
    extraRow.getCell('T').merge(row.getCell('T'));
    row.getCell('U').value = '36 Hour Check';
    nextRow.getCell('U').merge(row.getCell('U'));
    extraRow.getCell('U').merge(row.getCell('U'));
    row.getCell('V').value = '48 Hour Check and Tag Removed';
    nextRow.getCell('V').merge(row.getCell('V'));
    extraRow.getCell('V').merge(row.getCell('V'));

    [row, nextRow, extraRow].forEach((row) =>
      row.eachCell((cell) => {
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.font = HEADER_CELL_FONT;
      })
    );
    const partsRow = sheet.getRow(rowNumber - 1);
    partsRow.getCell('E').font = HEADER_CELL_FONT;
    partsRow.getCell('O').font = HEADER_CELL_FONT;
    partsRow.getCell('R').font = HEADER_CELL_FONT;
  }

  row.height = SIZE_BIG;
  nextRow.height = SIZE_MEDIUM;
  extraRow.height = SIZE_MEDIUM;

  return rowNumber + 3;
};

const addJoint = async (
  sheet: ExcelJS.Worksheet,
  joint: TJoint,
  rowNumber: number,
  type?: string
) => {
  const typeIsJI1 = type === 'JI1' ? true : false;
  const row = sheet.getRow(rowNumber);
  const nextRow = sheet.getRow(rowNumber + 1);

  if (typeIsJI1) {
    row.getCell('A').value = joint.jointNumber;
    nextRow.getCell('A').merge(row.getCell('A'));

    row.getCell('B').value = joint.tagId;
    nextRow.getCell('B').merge(row.getCell('B'));

    row.getCell('C').value = joint.pIdNo;
    nextRow.getCell('C').value = joint.additionalDetails;

    row.getCell('D').value = joint.flangeSize;
    nextRow.getCell('D').value = joint.rating;

    row.getCell('E').value = joint.boltDiameter;
    nextRow.getCell('E').value = joint.boltQuantity;

    row.getCell('F').value = joint.boltLength;
    nextRow.getCell('F').merge(row.getCell('F'));

    row.getCell('G').value = joint.boltMaterial;
    nextRow.getCell('G').merge(row.getCell('G'));

    row.getCell('H').value = joint.gasketType;
    nextRow.getCell('H').merge(row.getCell('H'));

    row.getCell('I').value = joint.torqueValue;
    nextRow.getCell('I').merge(row.getCell('I'));

    row.getCell('J').value = joint.hydraulicToolRef;
    nextRow.getCell('J').merge(row.getCell('J'));

    row.getCell('K').value = joint.hydraulicToolPass;
    nextRow.getCell('K').value = '';

    row.getCell('L').value = joint.hydraulicToolPumpPressure;
    nextRow.getCell('L').value = '';

    // Merge cells
    nextRow.getCell('N').merge(row.getCell('N'));
    nextRow.getCell('O').merge(row.getCell('O'));
    nextRow.getCell('Q').merge(row.getCell('Q'));
    nextRow.getCell('R').merge(row.getCell('R'));
    nextRow.getCell('T').merge(row.getCell('T'));
    nextRow.getCell('U').merge(row.getCell('U'));
    nextRow.getCell('X').merge(row.getCell('X'));
    nextRow.getCell('Y').merge(row.getCell('Y'));
    nextRow.getCell('Z').merge(row.getCell('Z'));
    nextRow.getCell('AA').merge(row.getCell('AA'));
    nextRow.getCell('AB').merge(row.getCell('AB'));

    //SA column
    row.getCell('U').value =
      joint.jointId === -1 ? '' : joint.supervisedAssembly === true ? 'Y' : 'N';
    row.getCell('V').value = joint.jointId === -1 || joint.supervisedAssembly === true ? '' : 'N/A';
    nextRow.getCell('V').value =
      joint.jointId === -1 || joint.supervisedAssembly === true ? '' : 'N/A';

    // Empty cell creation
    nextRow.getCell('M').value = '';
    nextRow.getCell('P').value = '';
    nextRow.getCell('S').value = '';
    nextRow.getCell('W').value = '';
  } else {
    row.getCell('A').value = joint.jointNumber;
    nextRow.getCell('A').merge(row.getCell('A'));

    row.getCell('B').value = joint.tagId;
    nextRow.getCell('B').merge(row.getCell('B'));

    row.getCell('C').value = joint.pIdNo;
    row.getCell('D').merge(row.getCell('C'));
    nextRow.getCell('C').value = joint.additionalDetails;
    nextRow.getCell('D').merge(nextRow.getCell('C'));

    // Merge cells
    nextRow.getCell('F').merge(row.getCell('F'));
    nextRow.getCell('G').merge(row.getCell('G'));
    nextRow.getCell('J').merge(row.getCell('J'));
    nextRow.getCell('N').merge(row.getCell('N'));
    nextRow.getCell('Q').merge(row.getCell('Q'));
    nextRow.getCell('R').merge(row.getCell('R'));
    nextRow.getCell('S').merge(row.getCell('S'));
    nextRow.getCell('T').merge(row.getCell('T'));
    nextRow.getCell('U').merge(row.getCell('U'));
    nextRow.getCell('V').merge(row.getCell('V'));

    row.getCell('I').merge(row.getCell('H'));
    nextRow.getCell('I').merge(nextRow.getCell('H'));
    row.getCell('M').merge(row.getCell('K'));
    nextRow.getCell('M').merge(nextRow.getCell('K'));
    row.getCell('P').merge(row.getCell('O'));
    nextRow.getCell('P').merge(nextRow.getCell('O'));

    // Empty cell creation
    nextRow.getCell('E').value = '';
    nextRow.getCell('H').value = '';
    nextRow.getCell('K').value = '';
    nextRow.getCell('O').value = '';
  }

  [row, nextRow].forEach((row) => {
    row.eachCell((cell) => (cell.alignment = { horizontal: 'center', vertical: 'middle' }));
    row.height = SIZE_BIG;
  });

  return rowNumber + 2;
};

const addFooter = async (sheet: ExcelJS.Worksheet, rowNumber: number, type: string) => {
  const [dividerRow, footerRow, nextRow, extraRow] = sheet.getRows(rowNumber, 4) || [];
  const typeIsJI1 = type === 'JI1' ? true : false;
  dividerRow.getCell('A').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFBFBFBF' },
  };
  if (typeIsJI1) {
    dividerRow.getCell('AB').merge(dividerRow.getCell('A'));

    footerRow.getCell('A').value = 'Comments:';
    extraRow.getCell('P').merge(footerRow.getCell('A'));

    footerRow.getCell('Q').value = 'Part A complete ?';
    footerRow.getCell('U').merge(footerRow.getCell('Q'));

    footerRow.getCell('V').value = 'Part B complete ?';
    footerRow.getCell('W').merge(footerRow.getCell('V'));

    footerRow.getCell('X').value = 'Part C complete ?';
    footerRow.getCell('AB').merge(footerRow.getCell('X'));

    nextRow.getCell('Q').value = 'MTL/PTL';
    nextRow.getCell('S').merge(nextRow.getCell('Q'));
    nextRow.getCell('T').value = 'Date';
    nextRow.getCell('U').merge(nextRow.getCell('T'));
    nextRow.getCell('V').value = 'PTL';
    nextRow.getCell('W').value = 'Date';
    nextRow.getCell('X').value = 'PTL/AA';
    nextRow.getCell('Z').merge(nextRow.getCell('X'));
    nextRow.getCell('AA').value = 'Date';
    nextRow.getCell('AB').merge(nextRow.getCell('AA'));

    extraRow.getCell('AB').merge(extraRow.getCell('Q'));
  } else {
    dividerRow.getCell('V').merge(dividerRow.getCell('A'));

    footerRow.getCell('A').value = 'Comments:';
    extraRow.getCell('I').merge(footerRow.getCell('A'));

    footerRow.getCell('J').value = 'Part A complete ?';
    footerRow.getCell('N').merge(footerRow.getCell('J'));

    footerRow.getCell('O').value = 'Part B complete ?';
    footerRow.getCell('Q').merge(footerRow.getCell('O'));

    footerRow.getCell('R').value = 'Part C complete ?';
    footerRow.getCell('V').merge(footerRow.getCell('R'));

    nextRow.getCell('J').value = 'MTL/PTL';
    nextRow.getCell('L').merge(nextRow.getCell('J'));
    nextRow.getCell('M').value = 'Date';
    nextRow.getCell('N').merge(nextRow.getCell('M'));
    nextRow.getCell('O').value = 'MTL/PTL';
    nextRow.getCell('P').merge(nextRow.getCell('O'));
    nextRow.getCell('Q').value = 'Date';
    nextRow.getCell('R').value = 'PTL/AA';
    nextRow.getCell('T').merge(nextRow.getCell('R'));
    nextRow.getCell('U').value = 'Date';
    nextRow.getCell('V').merge(nextRow.getCell('U'));

    extraRow.getCell('V').merge(extraRow.getCell('J'));
  }

  dividerRow.height = SIZE_BIG;
  footerRow.height = SIZE_BIG;
  nextRow.height = SIZE_BIG;
  extraRow.height = SIZE_HUGE;
  nextRow.font = HEADER_CELL_FONT;
  nextRow.eachCell((cell) => (cell.alignment = { vertical: 'top' }));
  footerRow.eachCell((cell) => (cell.alignment = { vertical: 'middle', horizontal: 'center' }));
  footerRow.getCell('A').alignment = { vertical: 'top' };

  return rowNumber + 4;
};

export const downloadWorkbook = async (workbook: ExcelJS.Workbook, fileName: string) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${fileName}.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
};
